/**
 * Predictive search — HopHopShop editorial overlay.
 *
 * Renders a full-viewport ink panel (60vh desktop / 100vh mobile) with a 32px
 * search input and a two-column suggestion layout below. Hits Shopify's
 * predictive search API for product suggestions and pivots between four
 * states: recent (default), loading, results, empty.
 * A query that matches nothing as typed is retried once with each word
 * stemmed to a prefix (see search-query.js), and every link out to the
 * results page asks for products only.
 *
 * Recent searches are stored in localStorage under "hhs:recent-searches"
 * (max 6, most-recent-first, deduped).
 */
import { Component } from "@theme/component";
import { broadenQuery } from "@theme/search-query";

// How long to wait for ZipChat's overlay after handing its hijacked input a
// focus event, before deciding it is not coming.
const ZIPCHAT_HANDOFF_MS = 400;

function zipchatSearchIsOpen() {
  const host = document.getElementById("zipchat-shadow-host");
  return Boolean(host?.shadowRoot?.getElementById("zipchat-search-overlay"));
}

class PredictiveSearch extends Component {
  static DEBOUNCE_MS = 250;
  static RECENT_KEY = "hhs:recent-searches";
  static RECENT_MAX = 6;
  static MIN_QUERY = 2;

  setup() {
    // Triggers live in the header (outside this custom element); query the
    // whole document and bind to every [data-open-search] button.
    this.openBtns = Array.from(document.querySelectorAll("[data-open-search]"));
    this.overlay = this.$("[data-search-overlay]");
    this.scrim = this.$("[data-search-scrim]");
    this.form = this.$("[data-search-form]");
    this.input = this.$("[data-search-input]");
    this.clearBtn = this.$("[data-search-clear]");
    this.closeBtns = this.$$("[data-close-search]");
    this.leftStates = {
      recent: this.$('[data-search-left] [data-state="recent"]'),
      loading: this.$('[data-search-left] [data-state="loading"]'),
      results: this.$('[data-search-left] [data-state="results"]'),
      empty: this.$('[data-search-left] [data-state="empty"]'),
    };
    this.rightStates = {
      default: this.$('[data-search-right] [data-state="default"]'),
      typing: this.$('[data-search-right] [data-state="typing"]'),
    };
    this.recentChipsEl = this.$("[data-recent-chips]");
    this.resultsGrid = this.$("[data-results-grid]");
    this.resultsCountEl = this.$("[data-results-count]");
    this.seeAllEl = this.$("[data-see-all]");
    this.moneyFormat = this.overlay?.dataset.moneyFormat || "{{amount}}";
    this.debounceTimer = null;
    this.searchSeq = 0;

    this.openBtns.forEach((btn) =>
      btn.addEventListener("click", () => this.open(btn)),
    );
    this.closeBtns.forEach((btn) =>
      btn.addEventListener("click", () => this.close()),
    );
    this.scrim?.addEventListener("click", () => this.close());
    this.clearBtn?.addEventListener("click", () => this.#clearInput());

    this.input?.addEventListener("input", () => {
      this.#syncClearButton();
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(
        () => this.#runSearch(),
        PredictiveSearch.DEBOUNCE_MS,
      );
    });

    this.form?.addEventListener("submit", (e) => this.#handleSubmit(e));

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) this.close();
    });
  }

  get isOpen() {
    return this.overlay?.classList.contains("is-open");
  }

  open(trigger) {
    if (!this.overlay) return;

    // ZipChat stamps every element its own search takes over. Its handler
    // cannot be relied on to suppress this one: stopping the event only
    // silences listeners registered after its own, and nothing orders an app
    // embed script against a theme module. Reading the stamp instead means
    // this overlay never appears for the frame before ZipChat's arrives. The
    // app deletes the stamp when it tears down, which restores this.
    if (trigger && trigger._zipchatSearchHijacked) return;

    // ZipChat can be pointed at this input rather than at the header button,
    // and then its overlay opens off a focus or pointer event on the input --
    // so this panel had to be shown just to hand one over, and was visible
    // for that moment. Focus the input where it stands instead: closed, this
    // overlay is only transparent and click-through, never display:none, so
    // the input still takes focus and their handler fires with nothing of
    // ours on screen.
    if (this.input && this.input._zipchatSearchHijacked) {
      this.input.focus({ preventScroll: true });
      // Should their overlay never materialise, the icon would be dead. Fall
      // back to this one rather than leave the shopper with no search at all.
      setTimeout(() => {
        if (!zipchatSearchIsOpen()) this.#show();
      }, ZIPCHAT_HANDOFF_MS);
      return;
    }

    this.#show();
  }

  #show() {
    this.overlay.classList.add("is-open");
    this.overlay.setAttribute("aria-hidden", "false");
    document.body.classList.add("hhs-search-open");
    this.#renderRecent();
    this.#showState("recent", "default");
    setTimeout(() => this.input?.focus(), 60);
  }

  close() {
    if (!this.overlay) return;
    this.overlay.classList.remove("is-open");
    this.overlay.setAttribute("aria-hidden", "true");
    document.body.classList.remove("hhs-search-open");
    this.#clearInput();
  }

  #clearInput() {
    // Nothing still in flight may paint over the recent-searches state.
    this.searchSeq++;
    if (this.input) this.input.value = "";
    this.#syncClearButton();
    this.#showState("recent", "default");
  }

  #syncClearButton() {
    if (!this.clearBtn) return;
    const has = (this.input?.value || "").length > 0;
    this.clearBtn.hidden = !has;
  }

  #showState(leftKey, rightKey) {
    Object.entries(this.leftStates).forEach(([k, el]) => {
      if (!el) return;
      el.hidden = k !== leftKey;
    });
    Object.entries(this.rightStates).forEach(([k, el]) => {
      if (!el) return;
      el.hidden = k !== rightKey;
    });
  }

  #handleSubmit(e) {
    const query = (this.input?.value || "").trim();
    if (!query) {
      e.preventDefault();
      return;
    }
    this.#pushRecent(query);
    // Let the form navigate to /search?q=…
  }

  async #runSearch() {
    const query = (this.input?.value || "").trim();
    if (query.length < PredictiveSearch.MIN_QUERY) {
      this.#renderRecent();
      this.#showState("recent", "default");
      return;
    }

    // Two requests can be in flight for one keystroke (the plain query and
    // its broadened retry); only the latest keystroke's answer may render.
    const seq = ++this.searchSeq;
    this.#showState("loading", "typing");

    try {
      let products = await this.#fetchProducts(query);

      // Nothing matched every word as typed. Titles carry colours in the
      // singular ("CRNA") and shoppers type the plural ("crne"), so retry
      // with each word stemmed to a prefix before giving up.
      if (products.length === 0) {
        const broadened = broadenQuery(query);
        if (broadened !== query) {
          products = await this.#fetchProducts(broadened);
        }
      }

      if (seq !== this.searchSeq) return;

      if (products.length === 0) {
        this.#showState("empty", "typing");
        return;
      }

      this.#renderResults(query, products);
      this.#showState("results", "typing");
    } catch (error) {
      if (seq !== this.searchSeq) return;
      console.error("Predictive search error:", error);
      this.#showState("empty", "typing");
    }
  }

  async #fetchProducts(query) {
    const response = await fetch(
      `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=hide`,
    );
    const data = await response.json();
    return data.resources?.results?.products || [];
  }

  #renderResults(query, products) {
    if (!this.resultsGrid) return;
    this.resultsGrid.innerHTML = products
      .map((p) => {
        const title = this.#escape(p.title);
        const url = this.#escape(p.url);
        const price = this.#formatMoney(p.price);
        const img = p.image
          ? `<img src="${this.#escape(p.image)}" alt="${title}" class="hhs-search__result-img" width="160" height="160" loading="lazy">`
          : "";

        // Compare-at price — predictive API exposes compare_at_price_max/min.
        // Show it struck-through only when it's genuinely higher than the price.
        const compareRaw =
          p.compare_at_price_max ||
          p.compare_at_price_min ||
          p.compare_at_price ||
          "";
        const priceNum = parseFloat(String(p.price).replace(/[^0-9.]/g, ""));
        const compareNum = parseFloat(String(compareRaw).replace(/[^0-9.]/g, ""));
        const onSale =
          !isNaN(priceNum) && !isNaN(compareNum) && compareNum > priceNum;
        const compareHtml = onSale
          ? `<span class="hhs-search__result-compare">${this.#formatMoney(compareRaw)}</span>`
          : "";

        return `
          <a href="${url}" class="hhs-search__result">
            <div class="hhs-search__result-thumb">${img}</div>
            <div class="hhs-search__result-body">
              <h4 class="hhs-search__result-title">${title}</h4>
              <span class="hhs-search__result-prices">
                <span class="hhs-search__result-price">${price}</span>
                ${compareHtml}
              </span>
            </div>
          </a>
        `;
      })
      .join("");

    if (this.seeAllEl) {
      this.seeAllEl.href = `/search?q=${encodeURIComponent(query)}&type=product&filter.v.availability=1`;
      const n = products.length;
      const tpl =
        n === 1
          ? this.seeAllEl.dataset.tplOne || ""
          : this.seeAllEl.dataset.tplMany || "";
      // CSS uppercases the link; keep the template's natural case here.
      this.seeAllEl.textContent = `${tpl.replace("__COUNT__", String(n))} →`;
    }
  }

  /* ── Recent searches ──────────────────────────────────────────── */
  #renderRecent() {
    if (!this.recentChipsEl) return;
    const recent = this.#readRecent();
    if (recent.length === 0) {
      // Copy comes from the snippet's data attribute so it stays translatable
      // (locales → predictive_search.no_recent).
      const emptyLabel = this.#escape(this.recentChipsEl.dataset.noRecent || "");
      this.recentChipsEl.innerHTML = `<div class="hhs-search__empty-chip">${emptyLabel}</div>`;
      return;
    }
    this.recentChipsEl.innerHTML = recent
      .map((term) => {
        const safe = this.#escape(term);
        const url = `/search?q=${encodeURIComponent(term)}&type=product&filter.v.availability=1`;
        return `
          <span class="hhs-chip-search">
            <a href="${url}" style="color:inherit;text-decoration:none;">${safe}</a>
            <button type="button" class="hhs-chip-search__remove" aria-label="Remove ${safe}" data-remove-recent="${safe}">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="square" stroke-linejoin="miter"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>
            </button>
          </span>
        `;
      })
      .join("");

    this.recentChipsEl
      .querySelectorAll("[data-remove-recent]")
      .forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.#removeRecent(btn.dataset.removeRecent);
          this.#renderRecent();
        });
      });
  }

  #pushRecent(term) {
    const clean = term.trim();
    if (!clean) return;
    let recent = this.#readRecent();
    recent = recent.filter((r) => r.toLowerCase() !== clean.toLowerCase());
    recent.unshift(clean);
    recent = recent.slice(0, PredictiveSearch.RECENT_MAX);
    this.#writeRecent(recent);
  }

  #removeRecent(term) {
    const recent = this.#readRecent().filter((r) => r !== term);
    this.#writeRecent(recent);
  }

  #readRecent() {
    try {
      const raw = localStorage.getItem(PredictiveSearch.RECENT_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }

  #writeRecent(arr) {
    try {
      localStorage.setItem(PredictiveSearch.RECENT_KEY, JSON.stringify(arr));
    } catch {
      /* localStorage unavailable / quota — ignore */
    }
  }

  #escape(str) {
    const el = document.createElement("span");
    el.textContent = str ?? "";
    return el.innerHTML;
  }

  /**
   * Format a price string from the predictive API (e.g. "4690.00") using the
   * shop's money_format, so results match the rest of the site (currency +
   * separators). Port of the canonical Shopify.formatMoney.
   */
  #formatMoney(input) {
    const format = this.moneyFormat || "{{amount}}";
    const amount = parseFloat(String(input).replace(/[^0-9.]/g, ""));
    if (isNaN(amount)) return this.#escape(String(input ?? ""));
    const cents = Math.round(amount * 100);

    const withDelimiters = (num, precision, thousands, decimal) => {
      const fixed = (num / 100).toFixed(precision);
      const parts = fixed.split(".");
      const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands);
      return whole + (parts[1] ? decimal + parts[1] : "");
    };

    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const token = match ? match[1] : "amount";
    let value;
    switch (token) {
      case "amount_no_decimals":
        value = withDelimiters(cents, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        value = withDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = withDelimiters(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = withDelimiters(cents, 2, "'", ".");
        break;
      case "amount_no_decimals_with_space_separator":
        value = withDelimiters(cents, 0, " ", ".");
        break;
      case "amount_with_space_separator":
        value = withDelimiters(cents, 2, " ", ",");
        break;
      default:
        value = withDelimiters(cents, 2, ",", ".");
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }
}

customElements.define("predictive-search", PredictiveSearch);

export { PredictiveSearch };
